import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core'
import { AmplitudeUnmaskDirective } from '../../../directives'
import { ColorName } from '../../design-system'
import { Icon, IconComponent } from '../../icon'
import { SubtleButtonComponent } from '../../subtle-button'
import { TertiaryButtonComponent } from '../../tertiary-button'

/**
 * Displays placeholder content when the actual content has either errored or is empty. Optional
 * `ng-content` is projected underneath the message, followed by any action buttons.
 *
 * @example
 * ```html
 * <ftr-placeholder-state
 *   title="No Recording Requests"
 *   message="When customers place requests for recordings, they will appear here."
 *   icon="RecordingRequest"
 *   actionText="Create Request"
 *   (action)="createRequest()"
 * />
 * ```
 */
@Component({
  selector: 'ftr-placeholder-state',
  hostDirectives: [AmplitudeUnmaskDirective],
  templateUrl: './placeholder-state.component.html',
  styleUrl: './placeholder-state.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IconComponent, SubtleButtonComponent, TertiaryButtonComponent],
})
export class PlaceholderStateComponent {
  readonly icon = input.required<Icon>()
  readonly iconColor = input.required<ColorName>()
  readonly title = input<string>()
  readonly message = input<string>()
  readonly size = input<PlaceholderStateSize>(PlaceholderStateSize.FullPage)
  readonly actionText = input<string>()
  readonly secondaryActionText = input<string>()

  readonly action = output()
  readonly secondaryAction = output()

  protected readonly isFullPage = computed<boolean>(() => this.size() === PlaceholderStateSize.FullPage)

  protected readonly highContrastColor = computed<ColorName>(() => {
    const iconColor = this.iconColor()
    switch (iconColor) {
      case 'Lemon50':
        return 'Lemon60'
      default:
        return iconColor
    }
  })

  protected readonly classes = computed(() => ({
    'placeholder-state': true,
    'placeholder-state--full-page': this.size() === PlaceholderStateSize.FullPage,
    'placeholder-state--medium': this.size() === PlaceholderStateSize.Medium,
    'placeholder-state--small': this.size() === PlaceholderStateSize.Small,
    'placeholder-state--tiny': this.size() === PlaceholderStateSize.Tiny,
  }))
}

export const PlaceholderStateSize = {
  FullPage: 'full-page',
  Medium: 'medium',
  Small: 'small',
  Tiny: 'tiny',
} as const
export type PlaceholderStateSize = (typeof PlaceholderStateSize)[keyof typeof PlaceholderStateSize]
